import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Flashcards} from '@trailhead-emu/design-system';
import {t} from 'lib/I18n';
import track from 'lib/tracking/track';

// Match LWC thc-flash-cards-ui pageLocation so existing flashcard_* dashboards
// keep working after the React hydration path takes over.
const PAGE_LOCATION = 'thc-flash-cards-ui';
const LABEL_SCOPE = 'views.trailhead.unit.flash_cards';

export default function FlashcardsHost({items}) {
  const rootRef = useRef(null);
  const indexRef = useRef(0);
  const startedRef = useRef(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (startedRef.current || items.length === 0) {
      return;
    }
    startedRef.current = true;
    track('flashcard_started', {
      pageLocation: PAGE_LOCATION,
      total_cards: items.length
    });
  }, [items.length]);

  // TDS2 Flashcards has onShuffle / onIndexChange but no onFlip. Mirror LWC by
  // listening for interactions on the flip control and reading aria-pressed.
  // Click and keyboard are separate: Enter/Space call handleFlip via onKeyDown
  // and do not synthesize a click.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return undefined;
    }

    const trackFlip = (flip) => {
      // Read pressed state before React re-renders. The control toggles, so the
      // side the learner sees after the flip is the opposite of the current value.
      const wasBack = flip.getAttribute('aria-pressed') === 'true';
      track('flashcard_card_flipped', {
        pageLocation: PAGE_LOCATION,
        card_index: indexRef.current,
        side: wasBack ? 'front' : 'back'
      });
    };

    const flipFromEvent = (event) => {
      const flip = event.target.closest?.('.tds2-flashcards__flip');
      if (!flip || !root.contains(flip)) {
        return null;
      }
      return flip;
    };

    const onFlipClick = (event) => {
      const flip = flipFromEvent(event);
      if (flip) {
        trackFlip(flip);
      }
    };

    const onFlipKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      const flip = flipFromEvent(event);
      if (flip) {
        trackFlip(flip);
      }
    };

    root.addEventListener('click', onFlipClick);
    root.addEventListener('keydown', onFlipKeyDown);
    return () => {
      root.removeEventListener('click', onFlipClick);
      root.removeEventListener('keydown', onFlipKeyDown);
    };
  }, []);

  const handleIndexChange = useCallback((next) => {
    const prev = indexRef.current;
    indexRef.current = next;
    setIndex(next);

    // LWC fires deck_completed when navigation lands on the last card.
    if (items.length > 0 && next === items.length - 1 && next > prev) {
      track('flashcard_deck_completed', {
        pageLocation: PAGE_LOCATION,
        total_cards: items.length
      });
    }
  }, [items.length]);

  const handleShuffle = useCallback(() => {
    track('flashcard_deck_shuffled', {
      pageLocation: PAGE_LOCATION,
      total_cards: items.length
    });
  }, [items.length]);

  return (
    <div ref={rootRef}>
      <Flashcards
        items={items}
        index={index}
        onIndexChange={handleIndexChange}
        onShuffle={handleShuffle}
        backNavLabel={t(`${LABEL_SCOPE}.back_nav_label`)}
        nextNavLabel={t(`${LABEL_SCOPE}.next_nav_label`)}
        shuffleNavLabel={t(`${LABEL_SCOPE}.shuffle_nav_label`)}
        navigationAriaLabel={t(`${LABEL_SCOPE}.navigation_aria_label`)}
      />
    </div>
  );
}

FlashcardsHost.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      front: PropTypes.node.isRequired,
      back: PropTypes.node.isRequired
    })
  ).isRequired
};
